/**
 * Theme preference utilities — shared across web, mobile shell, and tablet.
 */
import { isMobileShellLayout } from './mobileShellLayout';

export const DEFAULT_APP_THEME = 'dark';
const THEME_STORAGE_KEY = 'theme';

/** User's saved light/dark choice, or null if never set. */
export function getStoredThemePreference() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
  } catch (e) {
    // localStorage might be unavailable
  }
  return null;
}

/** Persist and apply theme on document (all shells). */
export function applyAppTheme(theme) {
  if (typeof document === 'undefined') {
    return DEFAULT_APP_THEME;
  }

  const next = theme === 'light' ? 'light' : 'dark';

  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch (e) {
    // ignore
  }

  document.body.className = `app theme-${next} theme`;
  if (isMobileShellLayout()) {
    document.body.classList.add('mobile-shell-theme');
  }
  document.documentElement.classList.toggle('theme-dark', next === 'dark');
  document.documentElement.classList.toggle('theme-light', next !== 'dark');

  window.dispatchEvent(new CustomEvent('roomnexa:theme-change', { detail: { theme: next } }));
  window.dispatchEvent(new Event('themechange'));

  return next;
}

export function toggleAppTheme() {
  return applyAppTheme(detectThemeSync() === 'dark' ? 'light' : 'dark');
}

/** Ensure first-time users get the same default on every device. */
export function ensureDefaultThemePreference() {
  if (getStoredThemePreference()) {
    return getStoredThemePreference();
  }
  return applyAppTheme(DEFAULT_APP_THEME);
}

/**
 * Detects the current theme from saved preference or document classes.
 * @returns {'dark' | 'light'}
 */
export const detectThemeSync = () => {
  if (typeof document === 'undefined') {
    return DEFAULT_APP_THEME;
  }

  const storedTheme = getStoredThemePreference();
  if (storedTheme) {
    return storedTheme;
  }

  const htmlClasses = document.documentElement.className || '';
  const bodyClasses = document.body.className || '';
  if (htmlClasses.includes('theme-dark') || bodyClasses.includes('theme-dark')) {
    return 'dark';
  }
  if (htmlClasses.includes('theme-light') || bodyClasses.includes('theme-light')) {
    return 'light';
  }

  return DEFAULT_APP_THEME;
};

/**
 * Sets up listeners for theme changes.
 * @param {Function} callback
 * @returns {Function} cleanup
 */
export const watchThemeChanges = (callback) => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => {};
  }

  let lastTheme = detectThemeSync();
  const notify = () => {
    const nextTheme = detectThemeSync();
    if (nextTheme === lastTheme) {
      return;
    }
    lastTheme = nextTheme;
    callback(nextTheme);
  };

  const bodyObserver = new MutationObserver(notify);
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });

  const htmlObserver = new MutationObserver(notify);
  htmlObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });

  const onWindowTheme = () => notify();
  window.addEventListener('roomnexa:theme-change', onWindowTheme);
  window.addEventListener('themechange', onWindowTheme);

  let mediaQuery;
  let onMediaChange;
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mediaQuery = window.matchMedia('(max-width:899px)');
    onMediaChange = () => notify();
    mediaQuery.addEventListener('change', onMediaChange);
  }

  return () => {
    bodyObserver.disconnect();
    htmlObserver.disconnect();
    window.removeEventListener('roomnexa:theme-change', onWindowTheme);
    window.removeEventListener('themechange', onWindowTheme);
    if (mediaQuery && onMediaChange) {
      mediaQuery.removeEventListener('change', onMediaChange);
    }
  };
};
